/**
 * Native WebSocket client with Socket.IO-like `.on` / `.off` for `/v1/ws/annotations`.
 */

import axios from 'axios';

type Handler = (data: unknown) => void;

const handlers = new Map<string, Set<Handler>>();
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
const maxReconnect = 12;

function buildWsUrl(): string {
  const rawBase = axios.defaults.baseURL || (typeof window !== 'undefined' ? window.location.origin : '');
  try {
    const u = new URL(rawBase || 'http://localhost');
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    const origin = `${u.protocol}//${u.host}`;
    return `${origin.replace(/\/$/, '')}/v1/ws/annotations`;
  } catch {
    return 'ws://localhost/v1/ws/annotations';
  }
}

function flushHandlers(): void {
  for (const set of handlers.values()) {
    set.clear();
  }
  handlers.clear();
}

function scheduleReconnect(): void {
  if (reconnectTimer) return;
  if (reconnectAttempt >= maxReconnect) return;
  const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000);
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function connect(): void {
  if (typeof WebSocket === 'undefined') return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  const url = buildWsUrl();
  try {
    ws = new WebSocket(url);
  } catch (e) {
    console.error('WebSocket create failed', e);
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    reconnectAttempt = 0;
    if (process.env.NODE_ENV === 'development') {
      console.log('Annotation WebSocket open:', url);
    }
  };

  ws.onmessage = (ev: MessageEvent) => {
    try {
      const msg = JSON.parse(String(ev.data));
      const eventName = msg.event as string;
      const payload = msg.data;
      const subs = handlers.get(eventName);
      if (subs) {
        subs.forEach((h) => {
          try {
            h(payload);
          } catch (err) {
            console.error('handler error', eventName, err);
          }
        });
      }
    } catch (err) {
      console.error('Bad WS frame', err, ev.data);
    }
  };

  ws.onerror = (err) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('Annotation WebSocket error', err);
    }
  };

  ws.onclose = () => {
    ws = null;
    if (process.env.NODE_ENV === 'development') {
      console.log('Annotation WebSocket closed; reconnecting…');
    }
    scheduleReconnect();
  };
}

export const socket = {
  on(event: string, handler: Handler): void {
    if (!handlers.has(event)) {
      handlers.set(event, new Set());
    }
    handlers.get(event)!.add(handler);
    connect();
  },

  off(event: string, handler: Handler): void {
    handlers.get(event)?.delete(handler);
  },

  // symmetry with socket.io-client; server only pushes
  emit(): void {
    /* no-op */
  },

  disconnect(): void {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempt = maxReconnect;
    ws?.close();
    ws = null;
    flushHandlers();
  },

  get connected(): boolean {
    return ws !== null && ws.readyState === WebSocket.OPEN;
  },
};
